import { ArrowLeft, MapPin, Settings, AlertTriangle, Thermometer } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const SiteOverrides = () => {
  const sites = [
    {
      id: 1,
      name: 'Downtown Restaurant',
      location: 'Oslo, Norway',
      lawPack: 'EU/NO Standard',
      overrides: {
        hotHoldingTemp: { enabled: true, value: '65°C', default: '63°C' },
        coldStorageTemp: { enabled: false, value: '4°C', default: '4°C' },
        checklistFrequency: { enabled: true, value: 'Every 2 hours', default: 'Every 4 hours' },
        signatureRequired: { enabled: true, value: true, default: false }
      },
      hasOverrides: true
    },
    {
      id: 2,
      name: 'Central Kitchen',
      location: 'Oslo, Norway',
      lawPack: 'EU/NO Standard',
      overrides: {
        hotHoldingTemp: { enabled: false, value: '63°C', default: '63°C' },
        coldStorageTemp: { enabled: true, value: '2°C', default: '4°C' },
        checklistFrequency: { enabled: false, value: 'Every 4 hours', default: 'Every 4 hours' },
        signatureRequired: { enabled: false, value: false, default: false }
      },
      hasOverrides: true
    },
    {
      id: 3,
      name: 'Catering Unit',
      location: 'Bergen, Norway',
      lawPack: 'EU/NO Standard',
      overrides: {
        hotHoldingTemp: { enabled: false, value: '63°C', default: '63°C' },
        coldStorageTemp: { enabled: false, value: '4°C', default: '4°C' },
        checklistFrequency: { enabled: false, value: 'Every 4 hours', default: 'Every 4 hours' },
        signatureRequired: { enabled: false, value: false, default: false }
      },
      hasOverrides: false
    }
  ];

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-8">
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>
          <h1 className="heading-section mb-2">Site-Specific Overrides</h1>
          <p className="text-muted-foreground">Configure per-site compliance rule overrides and customizations</p>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="glass-panel border-0">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold">{sites.length}</div>
                <div className="text-sm text-muted-foreground">Total Sites</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-panel border-0">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-400">
                  {sites.filter(s => s.hasOverrides).length}
                </div>
                <div className="text-sm text-muted-foreground">Sites with Overrides</div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-panel border-0">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">
                  {sites.filter(s => !s.hasOverrides).length}
                </div>
                <div className="text-sm text-muted-foreground">Using Default Settings</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Site Overrides */}
        <div className="space-y-6">
          {sites.map((site) => (
            <Card key={site.id} className="glass-panel border-0">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 glass-button rounded-lg">
                      <MapPin className="h-4 w-4" />
                    </div>
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {site.name}
                        {site.hasOverrides && (
                          <Badge className="bg-orange-500/20 text-orange-300">
                            Custom Settings
                          </Badge>
                        )}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        {site.location} • Law Pack: {site.lawPack}
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" className="glass-button">
                    <Settings className="h-4 w-4 mr-2" />
                    Configure
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Temperature Settings */}
                  <div className="space-y-4">
                    <h4 className="font-medium flex items-center gap-2">
                      <Thermometer className="h-4 w-4" />
                      Temperature Controls
                    </h4>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 glass-panel border border-white/10 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium">Hot Holding Temperature</span>
                            {site.overrides.hotHoldingTemp.enabled && (
                              <AlertTriangle className="h-3 w-3 text-orange-400" />
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Default: {site.overrides.hotHoldingTemp.default}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Input 
                            className="w-20 h-8 text-xs glass-input" 
                            value={site.overrides.hotHoldingTemp.value} 
                            disabled={!site.overrides.hotHoldingTemp.enabled}
                          />
                          <Switch 
                            checked={site.overrides.hotHoldingTemp.enabled} 
                          />
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 glass-panel border border-white/10 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium">Cold Storage Temperature</span>
                            {site.overrides.coldStorageTemp.enabled && (
                              <AlertTriangle className="h-3 w-3 text-orange-400" />
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Default: {site.overrides.coldStorageTemp.default}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Input 
                            className="w-20 h-8 text-xs glass-input" 
                            value={site.overrides.coldStorageTemp.value}
                            disabled={!site.overrides.coldStorageTemp.enabled}
                          />
                          <Switch 
                            checked={site.overrides.coldStorageTemp.enabled} 
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Operational Settings */}
                  <div className="space-y-4">
                    <h4 className="font-medium flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      Operational Controls
                    </h4>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 glass-panel border border-white/10 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium">Checklist Frequency</span>
                            {site.overrides.checklistFrequency.enabled && (
                              <AlertTriangle className="h-3 w-3 text-orange-400" />
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Default: {site.overrides.checklistFrequency.default}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Select disabled={!site.overrides.checklistFrequency.enabled}>
                            <SelectTrigger className="w-32 h-8 text-xs glass-input">
                              <SelectValue placeholder={site.overrides.checklistFrequency.value} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1hour">Every hour</SelectItem>
                              <SelectItem value="2hours">Every 2 hours</SelectItem>
                              <SelectItem value="4hours">Every 4 hours</SelectItem>
                              <SelectItem value="daily">Daily</SelectItem>
                            </SelectContent>
                          </Select>
                          <Switch 
                            checked={site.overrides.checklistFrequency.enabled} 
                          />
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between p-3 glass-panel border border-white/10 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium">Signature Required</span>
                            {site.overrides.signatureRequired.enabled && (
                              <AlertTriangle className="h-3 w-3 text-orange-400" />
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Default: {site.overrides.signatureRequired.default ? 'Required' : 'Optional'}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Switch 
                            checked={site.overrides.signatureRequired.value} 
                            disabled={!site.overrides.signatureRequired.enabled}
                          />
                          <Switch 
                            checked={site.overrides.signatureRequired.enabled} 
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-white/10">
                  <Button variant="outline" size="sm" className="glass-button">
                    Reset to Defaults
                  </Button>
                  <Button size="sm" className="cta-primary">
                    Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Warning Notice */}
        <Card className="glass-panel border-0 mt-8 border-l-4 border-l-orange-500">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5" />
              <div>
                <h4 className="font-medium mb-2">Important Notice</h4>
                <p className="text-sm text-muted-foreground">
                  Site-specific overrides modify standard compliance requirements. Ensure all changes comply with local food safety regulations. 
                  Operators remain fully responsible for compliance regardless of system settings.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SiteOverrides;